<script setup lang="ts">
type Props = {
  userCountry: string;
  userCurrency: string;
  userId: string | number;
};

const { userCountry, userCurrency, userId } = defineProps<Props>();

const { userCanOnlyWithdraw } = useUserLimits();
const { openDepositModal, openWithdrawModal } = usePayments();

const { walletIsLoading, walletRealFormatted, walletHasReal } = useWallet();

const { realWallet } = storeToRefs(useWalletStore());

const isWithdrawEnabledNow = computed(() => {
  return realWallet.value.withdraw_enable_now;
});

const isWithdrawEnabledForUser = computed(() => {
  return !!realWallet.value.withdrawEnabled;
});

const totalTimeWithdraw = computed(() => {
  const now = new Date();
  const expireDatetime = new Date(realWallet.value.withdraw_next_date);

  return expireDatetime.getTime() > now.getTime()
    ? (expireDatetime.getTime() - now.getTime()) / 1000
    : 0;
});
</script>

<template>
  <UserBox :class="$style.walletContainer">
    <div :class="$style.topContent">
      <div :class="$style.walletIcon">
        <NuxtIcon name="user/wallet" />
      </div>

      <span :class="$style.userCurrencyAndId">
        <DepositCurrencyButton
          :user-country="userCountry"
          :user-currency="userCurrency"
        />
        <div :class="$style.userIdDiv"> #{{ userId }} </div>
      </span>

      <strong :class="[$style.walletAmount, { 'opacity-30': walletIsLoading }]">
        {{ walletRealFormatted }}
      </strong>
    </div>

    <div :class="$style.walletBoxBtns">
      <button
        :class="[$style.walletBtn, $style.depositBtn]"
        :disabled="userCanOnlyWithdraw"
        @click="openDepositModal()"
      >
        {{ $t('account.wallet.deposit') }}
      </button>

      <template v-if="walletIsLoading">
        <button
          id="btn_loading"
          :class="[$style.walletBtn, $style.withdrawBtn, $style.loading]"
          disabled
        >
          <NuxtIcon
            :class="[$style.icon, `rotate-animation`]"
            name="sync"
          />
          {{ $t('account.wallet.withdraw') }}
        </button>
      </template>

      <template v-else-if="!isWithdrawEnabledNow && totalTimeWithdraw">
        <WidgetsCountdown
          class="flex-1 min-h-full flex items-stretch"
          :restart="false"
          :total-time="totalTimeWithdraw"
        >
          <template #process="{ time }">
            <div
              :class="[
                $style.walletBtn,
                $style.withdrawBtn,
                $style.isCountdown,
              ]"
            >
              <span class="text-[.65rem] leading-3">
                {{ $t('account.wallet.wallet_withdraw_time') }}
              </span>
              <span class="text-error text-xs font-bold leading-3">
                {{ `${time.hours}h:${time.minutes}m:${time.seconds}s` }}
              </span>
            </div>
          </template>
          <template #finish>
            <button
              v-if="walletHasReal"
              id="btn_finish_countdown"
              :class="[$style.walletBtn, $style.withdrawBtn]"
              :disabled="!isWithdrawEnabledForUser"
              @click="openWithdrawModal()"
            >
              {{ $t('account.wallet.withdraw') }}
            </button>
          </template>
        </WidgetsCountdown>
      </template>

      <template v-else-if="isWithdrawEnabledForUser && walletHasReal">
        <button
          id="btn_else_if"
          :class="[$style.walletBtn, $style.withdrawBtn]"
          @click="openWithdrawModal()"
        >
          {{ $t('account.wallet.withdraw') }}
        </button>
      </template>

      <template v-else>
        <button
          id="btn_else_disabled"
          :class="[$style.walletBtn, $style.withdrawBtn]"
          disabled
        >
          {{ $t('account.wallet.withdraw') }}
        </button>
      </template>
    </div>
  </UserBox>
</template>

<style module lang="postcss">
.walletContainer {
  @apply min-w-[70vw] md:min-w-[365px] leading-[100%] min-h-full flex flex-col;

  .topContent {
    @apply pb-2 relative flex flex-col items-start justify-start;

    .walletIcon {
      @apply absolute text-texts opacity-20 top-0 lg:top-2 right-0 lg:right-2  w-8 lg:w-10 h-8 lg:h-10;

      svg {
        @apply w-full h-full;
      }
    }

    .userCurrencyAndId {
      @apply text-texts/70 inline-flex pb-2;

      .userIdDiv {
        @apply text-texts/50 font-medium justify-end text-right self-end text-xs ml-3;
      }
    }

    .walletAmount {
      @apply py-1 inline-block text-3xl text-texts leading-none font-medium lg:text-4xl pr-16 md:pr-24 pb-2;
    }
  }

  .walletBoxBtns {
    @apply flex items-stretch justify-between gap-2 w-full max-w-[400px] mt-auto;

    .walletBtn {
      @apply py-2 px-4 rounded-md flex-1 min-w-28 text-sm md:text-base flex items-center justify-center gap-2;

      &.depositBtn {
        @apply bg-primary text-button-text font-bold;

        &[disabled]:not([disabled='false']) {
          @apply opacity-40 cursor-not-allowed;
        }
      }

      &.withdrawBtn {
        @apply bg-white text-bg-primary font-semibold;

        &[disabled]:not([disabled='false']) {
          @apply opacity-40 cursor-not-allowed;
        }

        &.isCountdown {
          @apply flex flex-col items-center gap-1 px-3 py-0.5 cursor-not-allowed flex-1 min-h-full;
        }

        .icon {
          @apply text-sm;
        }
      }
    }
  }
}
</style>
